/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {CardMedia, createStyles, Paper, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo} from 'react';
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type CarouselItemClassKey = 'root' | 'coverLeft' | 'coverRight' | 'divMid' | 'title' | 'link';

const useStyles = makeStyles<Theme, {}, CarouselItemClassKey>(
  (): StyleRules<CarouselItemClassKey> =>
    createStyles({
      root: {
        display: 'flex',
        flexDirection: 'row',
        transition: 'opacity .25s ease',
        '&:hover': {
          opacity: 0.85
        },
        // Below 700px the two side-by-side photos + title band become too
        // narrow to read; stack them into a single vertical column instead.
        '@media (max-width: 700px)': {
          flexDirection: 'column'
        }
      },
      // Fixed pixel heights here used to pair with a flexible (flex: 1)
      // width, so the box's own aspect ratio silently changed with every
      // viewport width — cropping the same photo differently depending on
      // screen size. All slide photos share one native ratio (340×474), so
      // pinning the box to that exact ratio keeps width and height moving
      // together at any screen size, with no crop at all.
      coverLeft: {
        flex: 1,
        aspectRatio: '340 / 474',
        borderTop: 'solid 1px white',
        borderBottom: 'solid 1px white',
        borderLeft: 'solid 1px white',
        // Stacked layout: this was the strip's left edge, but now it's the
        // top block of a column, so it needs a right edge too to stay a
        // closed box rather than bleeding into divMid.
        '@media (max-width: 700px)': {
          borderRight: 'solid 1px white'
        }
      },
      coverRight: {
        flex: 1,
        aspectRatio: '340 / 474',
        borderTop: 'solid 1px white',
        borderBottom: 'solid 1px white',
        borderRight: 'solid 1px white',
        '@media (max-width: 700px)': {
          borderLeft: 'solid 1px white'
        }
      },
      title: {
        color: 'white',
        textDecoration: 'underline',
        fontSize: 'larger',
        textAlign: 'center'
      },
      divMid: {
        borderTop: 'solid 1px white',
        borderBottom: 'solid 1px white',
        backgroundColor: '#1d1d1d',
        display: 'flex',
        flex: '0.5',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 700px)': {
          borderLeft: 'solid 1px white',
          borderRight: 'solid 1px white',
          // Was sized by flex-grow sharing the row's width; stacked
          // vertically there's no extra space to grow into, so size to
          // content with real padding instead of collapsing to a sliver.
          flex: '0 0 auto',
          padding: '14px 0'
        }
      },
      link: {
        display: 'block',
        textDecoration: 'none',
        cursor: 'pointer'
      }
    }),
  {
    name: 'CarouselItem'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type CarouselItemProps = {
  className?: string;
  pictureLeft: string;
  pictureRight: string;
  title: string;
  id: number;
};

export default memo(({className, title, pictureLeft, pictureRight, id}: CarouselItemProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');

  return (
    <Link className={classes.link} to={`/galleries?id=${id}`} aria-label={`${title} — ${t("home.visit")}`}>
      <Paper className={classNames(classes.root, className)}>
        <CardMedia
          className={classes.coverLeft}
          image={asset(pictureLeft)}
        />
        <div className={classes.divMid}>
          <div className={classes.title}>{title}</div>
        </div>
        <CardMedia
          className={classes.coverRight}
          image={asset(pictureRight)}
        />
      </Paper>
    </Link>
  );
});
